import React, { useEffect, useState } from "react";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { Box, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import styles from "./RichTextEditor.module.css";

interface CustomRichTextEditorProps {
  value?: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  minHeight?: number;
}

const CustomRichTextEditor: React.FC<CustomRichTextEditorProps> = ({
  value = "",
  onChange,
  label,
  placeholder = "Start typing...",
  minHeight = 150,
}) => {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure heading to allow levels 1-4
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder: placeholder,
        emptyEditorClass: styles.isEditorEmpty,
      }),
    ],
    content: value || "<p></p>",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
  });

  // Update editor content when value prop changes
  // Note: We only depend on 'value' to avoid infinite loops with the editor instance
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      const contentToSet = value || "<p></p>";
      editor.commands.setContent(contentToSet, { emitUpdate: false });
    }
  }, [value, editor]);

  return (
    <Box>
      {label && (
        <Box
          component="label"
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: 500,
            marginBottom: "8px",
            color: "var(--mantine-color-text)",
          }}
        >
          {label}
        </Box>
      )}
      <RichTextEditor editor={editor}>
        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          {/* Basic Formatting - Always Visible */}
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
          </RichTextEditor.ControlsGroup>

          {/* Toggle Advanced Options */}
          <RichTextEditor.ControlsGroup>
            <UnstyledButton
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "4px",
                color: "var(--mantine-color-text)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "var(--mantine-color-gray-1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {showAdvancedOptions ? (
                <IconChevronUp size={18} />
              ) : (
                <IconChevronDown size={18} />
              )}
            </UnstyledButton>
          </RichTextEditor.ControlsGroup>

          {/* Advanced Options - Conditionally Rendered */}
          {showAdvancedOptions && (
            <>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ClearFormatting />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 />
                <RichTextEditor.H2 />
                <RichTextEditor.H3 />
                <RichTextEditor.H4 />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
                <RichTextEditor.Hr />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.AlignLeft />
                <RichTextEditor.AlignCenter />
                <RichTextEditor.AlignRight />
                <RichTextEditor.AlignJustify />
              </RichTextEditor.ControlsGroup>
            </>
          )}
        </RichTextEditor.Toolbar>

        <RichTextEditor.Content
          data-placeholder={placeholder}
          style={{
            minHeight: `${minHeight}px`,
          }}
        />
      </RichTextEditor>
    </Box>
  );
};

export default CustomRichTextEditor;
